import React from "react";
import {
  BrowserRouter as Router,
  Routes,
  Route,
  Outlet,
  Navigate,
} from "react-router-dom";
import MainLayout from "./layout/MainLayout";
import DashboardLayout from "./layout/DashboardLayout";
import ProtectedRoute from "./components/ProtectedRoute";
import { UserProvider } from "./contexts/UserContext";
import { FeaturesProvider } from "./contexts/FeaturesContext";
import Home from "./pages/Home";
import Login from "./pages/Login";
import Signup from "./pages/Signup";
import ForgotPassword from "./pages/ForgotPassword";
import AuthShell from "./components/auth/AuthLayout";
import { Slide, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import VenueDetails from "./pages/VenueDetails";
import Profile from "./pages/Profile";
import EditVenues from "./pages/EditVenues";
import Dashboard from "./pages/Dashboard";
import Staff from "./pages/Staff";
import InvoicePdf from "./pages/invoicePdf";
import Appointment from "./pages/Appointment";
import Clients from "./pages/clients/Clients";
import Community from "./pages/clients/Community";
import PromoCode from "./pages/PromoCode";
import CLientsInfo from "./pages/clients/ClientsInfo";
import Invoices from "./pages/finance/Invoices";
import Expenses from "./pages/finance/Expenses";
import Reports from "./pages/finance/Reports";
import Specialities from "./pages/settings/Specialities";
import Services from "./pages/settings/Services";
import Currencies from "./pages/settings/Currencies";
import Units from "./pages/settings/Units";
import UserNotifications from "./pages/UserNotifications";
import Advanced from "./pages/settings/Advanced";
import Loyalty from "./pages/settings/Loyalty";
import { useInactivityTimeout } from "./hooks/useInactivityTimeout";
import Banners from "./pages/Banners";
import AdminData from "./pages/AdminData";
import RevenueReport from "./pages/finance/reports/RevenueReport";
import ProfitLossReport from "./pages/finance/reports/ProfitLossReport";
import BalanceSheetReport from "./pages/finance/reports/BalanceSheetReport";
import Payments from "./pages/finance/reports/Payments";
import Notes from "./pages/finance/NotesPage";
import RefundsPage from "./pages/finance/RefundsPage";
import AdminInvoices from "./pages/AdminInvoices";
import AdminExpenses from "./pages/AdminExpenses";
import AdminReports from "./pages/AdminReports";
import AdminNotificationDevices from "./pages/AdminNotificationDevices";
import ClientDetails from "./pages/clients/ClientDetails";
import Stocks from "./pages/Stocks";
import Products from "./pages/Products";
import Purchases from "./pages/Purchases";
import PinProtectedRoute from "./components/PinProtectedRoute";
import SupplierDetails from "./pages/suppliers/SupplierDetails";
import AdminAppointmentSocketListener from "./components/AdminAppointmentSocketListener";
import BackofficeWebPushRegistration from "./components/BackofficeWebPushRegistration";
import PointInvitation from "./pages/PointInvitation";
import PublicMatch from "./pages/PublicMatch";
import Matchmaking from "./pages/Matchmaking";
import Coaches from "./pages/classes/Coaches";
import ClassesPage from "./pages/classes/Classes";
import Packages from "./pages/classes/Packages";
import ClientPackages from "./pages/classes/ClientPackages";
import SuperadminProtectedRoute from "./components/superadmin/SuperadminProtectedRoute";
import SuperadminLayout from "./layout/SuperadminLayout";
import SuperadminOrganizations from "./pages/superadmin/Organizations";
import SuperadminOrganizationDetails from "./pages/superadmin/OrganizationDetails";
import SuperadminFeatures from "./pages/superadmin/Features";
import FeatureRoute from "./components/features/FeatureRoute";
import { ThemeProvider } from "@mui/material/styles";
import { appTheme } from "./assets/theme";

const AppContent: React.FC = () => {
  useInactivityTimeout();

  return (
    <>
      <ToastContainer
        position="top-right"
        transition={Slide}
        newestOnTop
      />
      <AdminAppointmentSocketListener />
      <BackofficeWebPushRegistration />
      <Routes>
        {/* Public routes */}
        {/* Auth pages share one layout so switching between them animates
            instead of reloading. */}
        <Route element={<AuthShell />}>
          <Route path="/login" element={<Login />} />
          <Route path="/signup" element={<Signup />} />
          <Route path="/forgot-password" element={<ForgotPassword />} />
        </Route>
        <Route path="/invite/:token" element={<PointInvitation />} />
        <Route path="/match/:matchmakingId" element={<PublicMatch />} />

        {/* Platform (superadmin) panel — its own token, no organization context */}
        <Route
          path="/superadmin"
          element={
            <SuperadminProtectedRoute>
              <SuperadminLayout />
            </SuperadminProtectedRoute>
          }
        >
          <Route index element={<Navigate to="organizations" replace />} />
          <Route path="organizations" element={<SuperadminOrganizations />} />
          <Route
            path="organizations/:organizationId"
            element={<SuperadminOrganizationDetails />}
          />
          <Route path="features" element={<SuperadminFeatures />} />
        </Route>

        {/* Protected routes with MainLayout */}
        <Route
          element={
            <ProtectedRoute>
              <MainLayout>
                <Outlet />
              </MainLayout>
            </ProtectedRoute>
          }
        >
          <Route path="/" element={<Home />} />
          <Route path="/home" element={<Home />} />
          <Route path="/venue-details" element={<VenueDetails />} />
          <Route path="/profile" element={<Profile />} />
          <Route path="/edit-venues" element={<EditVenues />} />
          <Route path="/all-data" element={<AdminData />} />
          <Route path="/all-data/invoices" element={<AdminInvoices />} />
          <Route path="/all-data/expenses" element={<AdminExpenses />} />
          <Route path="/all-data/reports" element={<AdminReports />} />
          <Route
            path="/all-data/notification-devices"
            element={<AdminNotificationDevices />}
          />
        </Route>

        {/* Protected Dashboard routes */}
        <Route
          path="/dashboard/:businessId/*"
          element={
            <ProtectedRoute>
              <DashboardLayout />
            </ProtectedRoute>
          }
        >
          <Route index element={<Dashboard />} />
          <Route path="overview" element={<Dashboard />} />
          <Route path="staff" element={<Staff />} />
          <Route path="appointment" element={<Appointment />} />
          <Route path="settings" element={<Dashboard />} />
          <Route path="banners" element={<Banners />} />
          <Route path="invoicepdf/:id" element={<InvoicePdf />} />
          <Route path="userNotifications" element={<UserNotifications />} />
          <Route path="clients/clients" element={<Clients />} />
          <Route path="clients/details/:clientId" element={<ClientDetails />} />
          <Route path="clients/community" element={<Community />} />
          <Route path="matchmaking" element={<Matchmaking />} />
          <Route
            path="stock"
            element={
              <FeatureRoute feature="STOCK">
                <PinProtectedRoute>
                  <Stocks />
                </PinProtectedRoute>
              </FeatureRoute>
            }
          />
          <Route
            path="products"
            element={
              <FeatureRoute feature="STOCK">
                <PinProtectedRoute>
                  <Products />
                </PinProtectedRoute>
              </FeatureRoute>
            }
          />
          <Route
            path="purchases"
            element={
              <FeatureRoute feature="STOCK">
                <PinProtectedRoute>
                  <Purchases />
                </PinProtectedRoute>
              </FeatureRoute>
            }
          />
          <Route path="suppliers/details/:supplierId" element={<SupplierDetails />} />
          <Route
            path="classes/coaches"
            element={
              <FeatureRoute feature="CLASSES">
                <Coaches />
              </FeatureRoute>
            }
          />
          <Route
            path="classes/classes"
            element={
              <FeatureRoute feature="CLASSES">
                <ClassesPage />
              </FeatureRoute>
            }
          />
          <Route
            path="classes/packages"
            element={
              <FeatureRoute feature="CLASSES">
                <Packages />
              </FeatureRoute>
            }
          />
          <Route
            path="classes/client-packages"
            element={
              <FeatureRoute feature="CLASSES">
                <ClientPackages />
              </FeatureRoute>
            }
          />
          <Route path="promocode" element={<PromoCode />} />
          <Route path="clients/info" element={<CLientsInfo />} />
          <Route
            path="finance/invoices"
            element={
              <PinProtectedRoute>
                <Invoices />
              </PinProtectedRoute>
            }
          />
          <Route
            path="finance/expenses"
            element={
              <PinProtectedRoute>
                <Expenses />
              </PinProtectedRoute>
            }
          />
          <Route
            path="finance/reports"
            element={
              <PinProtectedRoute>
                <Reports />
              </PinProtectedRoute>
            }
          />
          <Route
            path="finance/refunds"
            element={
              <PinProtectedRoute>
                <RefundsPage />
              </PinProtectedRoute>
            }
          />
          <Route
            path="reports/revenue"
            element={
              <PinProtectedRoute>
                <RevenueReport />
              </PinProtectedRoute>
            }
          />
          <Route
            path="reports/pnl"
            element={
              <PinProtectedRoute>
                <ProfitLossReport />
              </PinProtectedRoute>
            }
          />
          <Route
            path="reports/balance"
            element={
              <PinProtectedRoute>
                <BalanceSheetReport />
              </PinProtectedRoute>
            }
          />
          <Route
            path="reports/payments"
            element={
              <PinProtectedRoute>
                <Payments />
              </PinProtectedRoute>
            }
          />
          <Route
            path="reports/notes"
            element={
              <PinProtectedRoute>
                <Notes />
              </PinProtectedRoute>
            }
          />
          <Route path="settings/specialities" element={<Specialities />} />
          <Route path="settings/services" element={<Services />} />
          <Route path="settings/currencies" element={<Currencies />} />
          <Route path="settings/units" element={<Units />} />
          <Route path="settings/advanced" element={<Advanced />} />
          <Route
            path="loyalty/settings"
            element={
              <FeatureRoute feature="LOYALTY">
                <Loyalty section="settings" />
              </FeatureRoute>
            }
          />
          <Route
            path="loyalty/clients"
            element={
              <FeatureRoute feature="LOYALTY">
                <Loyalty section="clients" />
              </FeatureRoute>
            }
          />
          {/* Loyalty moved out of Settings; keep old links working. */}
          <Route
            path="settings/loyalty"
            element={<Navigate to="../loyalty/settings" replace />}
          />
        </Route>
      </Routes>
    </>
  );
};

const App: React.FC = () => (
  <ThemeProvider theme={appTheme}>
    <Router>
      <UserProvider>
        <FeaturesProvider>
          <AppContent />
        </FeaturesProvider>
      </UserProvider>
    </Router>
  </ThemeProvider>
);

export default App;
